import styled from "styled-components";
import { Link } from "react-router-dom";
import { dashboardTheme } from "../theme/dashboardTheme";

export const ProfileCard = styled.div<{ $horizontal?: boolean }>`
  display: flex;
  flex-direction: ${({ $horizontal }) => ($horizontal ? "row" : "column")};
  align-items: ${({ $horizontal }) => ($horizontal ? "center" : "stretch")};
  width: 100%;
  gap: ${({ $horizontal }) => ($horizontal ? "1.5rem" : "1.25rem")};

  @media (max-width: ${({ theme }) => theme.breakpoints.tabletLg}) {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
`;

export const ProfileSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
  min-width: 0;
  padding-right: 0.5rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.tabletLg}) {
    min-width: 13rem;
    max-width: 16rem;
  }
`;

export const ProfileTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
  flex: 1;
`;

export const WelcomeTitle = styled.h2`
  margin: 0;
  font-size: clamp(1rem, 1.8vw, 1.2rem);
  font-weight: 700;
  color: ${dashboardTheme.colors.textPrimary};
  line-height: 1.3;
`;

export const RolePill = styled.span`
  display: inline-flex;
  align-self: flex-start;
  padding: 0.3rem 0.75rem;
  border-radius: ${dashboardTheme.radius.pill};
  background: ${dashboardTheme.colors.surfaceElevated};
  border: 1px solid ${dashboardTheme.colors.cardBorder};
  color: ${dashboardTheme.colors.textSecondary};
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const ProfileLink = styled(Link)`
  font-size: 0.8rem;
  color: ${dashboardTheme.colors.accent};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export const AvatarCircle = styled.div<{ $compact?: boolean }>`
  flex-shrink: 0;
  width: ${({ $compact }) => ($compact ? "3rem" : "3.5rem")};
  height: ${({ $compact }) => ($compact ? "3rem" : "3.5rem")};
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    ${dashboardTheme.colors.accent} 0%,
    ${dashboardTheme.colors.chartLine} 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $compact }) => ($compact ? "1.25rem" : "1.5rem")};
  font-weight: 700;
  color: #ffffff;
  box-shadow: ${dashboardTheme.shadow.metric};
`;

export const HeroMetricCard = styled.div<{ $inline?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: ${({ $inline }) => ($inline ? "1rem 1.25rem" : "1.25rem 1rem")};
  border-radius: ${dashboardTheme.radius.metric};
  background: linear-gradient(
    135deg,
    rgba(110, 168, 255, 0.18) 0%,
    rgba(110, 168, 255, 0.06) 100%
  );
  border: 1px solid ${dashboardTheme.colors.accentBorder};
  flex-shrink: 0;
  justify-content: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.tabletLg}) {
    min-width: 11rem;
  }
`;

export const HeroMetricInfoAnchor = styled.div`
  position: absolute;
  top: 0.4rem;
  right: 0.4rem;
  z-index: 1;
`;

export const HeroMetricLabel = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${dashboardTheme.colors.textSecondary};
  line-height: 1.3;
`;

export const HeroMetricLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-right: 1.75rem;
`;

export const HeroMetricValue = styled.span`
  font-size: clamp(1.1rem, 1.8vw, 1.5rem);
  font-weight: 700;
  color: ${dashboardTheme.colors.textPrimary};
  line-height: 1.15;
  word-break: break-word;
`;

export const MetricsGrid = styled.div<{ $horizontal?: boolean }>`
  display: grid;
  grid-template-columns: ${({ $horizontal }) =>
    $horizontal ? "repeat(5, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))"};
  gap: ${({ $horizontal }) => ($horizontal ? "0.75rem" : "0.65rem")};
  width: 100%;
  flex: 1;
  min-width: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.tabletLg}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.65rem;
  }
`;

export const MetricCard = styled.div<{ $compact?: boolean; $clickable?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: ${({ $compact }) => ($compact ? "0.85rem 0.75rem" : "0.9rem 0.85rem")};
  border-radius: ${dashboardTheme.radius.metric};
  background: ${dashboardTheme.colors.surface};
  border: 1px solid ${dashboardTheme.colors.cardBorder};
  min-height: ${({ $compact }) => ($compact ? "4rem" : "4.5rem")};
  justify-content: center;
  min-width: 0;
  text-decoration: none;
  color: inherit;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  transition: border-color 0.15s ease;

  ${({ $clickable }) =>
    $clickable &&
    `
    &:hover {
      border-color: ${dashboardTheme.colors.accent};
    }
  `}
`;

export const MetricLabel = styled.span`
  font-size: 0.6rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${dashboardTheme.colors.textMuted};
  line-height: 1.35;
`;

export const MetricValue = styled.span`
  font-size: clamp(1rem, 1.4vw, 1.2rem);
  font-weight: 700;
  color: ${dashboardTheme.colors.textPrimary};
  line-height: 1.15;
`;

export const MetricsBlock = styled.div<{ $horizontal?: boolean }>`
  display: flex;
  flex-direction: ${({ $horizontal }) => ($horizontal ? "row" : "column")};
  align-items: stretch;
  gap: ${({ $horizontal }) => ($horizontal ? "0.875rem" : "0.75rem")};
  flex: 1;
  min-width: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.tabletLg}) {
    flex-direction: column;
  }
`;

export const ActionsRow = styled.div<{ $horizontal?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  flex-shrink: 0;
  justify-content: center;
  padding-left: ${({ $horizontal }) => ($horizontal ? "0.5rem" : "0")};
  min-width: ${({ $horizontal }) => ($horizontal ? "8.5rem" : "auto")};
  width: ${({ $horizontal }) => ($horizontal ? "auto" : "100%")};

  @media (max-width: ${({ theme }) => theme.breakpoints.tabletLg}) {
    flex-direction: row;
    padding-left: 0;
    min-width: auto;
    width: 100%;
  }
`;

export const PrimaryAction = styled.a`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.7rem 0.875rem;
  border-radius: ${dashboardTheme.radius.pill};
  background: ${dashboardTheme.colors.accent};
  color: #0d1117;
  font-size: 0.75rem;
  font-weight: 700;
  text-decoration: none;
  text-align: center;
  white-space: nowrap;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tabletLg}) {
    padding: 0.75rem 1rem;
    font-size: 0.8rem;
  }
`;

export const SecondaryAction = styled.a`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.7rem 0.875rem;
  border-radius: ${dashboardTheme.radius.pill};
  background: transparent;
  border: 1px solid ${dashboardTheme.colors.cardBorder};
  color: ${dashboardTheme.colors.textPrimary};
  font-size: 0.75rem;
  font-weight: 600;
  text-decoration: none;
  text-align: center;
  white-space: nowrap;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover {
    border-color: ${dashboardTheme.colors.accentBorder};
    background: ${dashboardTheme.colors.accentSoft};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tabletLg}) {
    padding: 0.75rem 1rem;
    font-size: 0.8rem;
  }
`;

export const CardDivider = styled.hr<{ $horizontal?: boolean }>`
  width: ${({ $horizontal }) => ($horizontal ? "1px" : "100%")};
  height: ${({ $horizontal }) => ($horizontal ? "auto" : "1px")};
  border: none;
  border-top: ${({ $horizontal }) => ($horizontal ? "none" : `1px solid ${dashboardTheme.colors.divider}`)};
  border-left: ${({ $horizontal }) => ($horizontal ? `1px solid ${dashboardTheme.colors.divider}` : "none")};
  margin: ${({ $horizontal }) => ($horizontal ? "0 0.25rem" : "0")};
  align-self: stretch;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.tabletLg}) {
    width: 100%;
    height: 1px;
    border-top: 1px solid ${dashboardTheme.colors.divider};
    border-left: none;
    margin: 0;
  }
`;
